import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup"; // Import Yup for validation
import CloseIcon from "@mui/icons-material/Close";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { postParentTask, updateParentTask } from "../../services/parentTask";
import { getProject } from "../../services/project.service";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import { getTokenbyData } from "../../services/user.service";

const AddParentTask = (props) => {
  const { selectedData, formType, setOpenDrawer, GetData } = props;
  const formRef = useRef(null);
  const { accessToken } = useAuth();

  const [projectData, setProjectData] = useState([]);
  const [isProjectLead, setIsProjectLead] = useState(true); // Track if user is project lead
  const [initialValues, setInitialValues] = useState({
    project: "",
    projectType: "",
    title: "",
    description: "",
  });

  const validationSchema = Yup.object().shape({
    project: Yup.string().required("Project is required"),
    projectType: Yup.string().required("Project type is required"),
    title: Yup.string()
      .required("Enter Title")
      .test("max-words", "Title cannot exceed 10 words", (value) => {
        if (!value) return true; // Yup.required will handle empty
        const wordCount = value.trim().split(/\s+/).length; // split by spaces
        return wordCount <= 10;
      }),

    // description: Yup.string().required("Enter Description"),
  });

  useEffect(() => {
    if (formType === "view" || formType === "edit") {
      setInitialValues({
        project: selectedData.project || "",
        projectType: typeof selectedData.projectType === "object"
          ? selectedData.projectType?._id
          : selectedData.projectType || "",
        title: selectedData.title || "",
        description: selectedData.description || "",
      });

      // Check if current user is project lead when editing
      if (formType === "edit") {
        checkIfProjectLead();
      }
    }
  }, [formType, selectedData]);

  // Check if current user is project lead of the selected parent task's project
  const checkIfProjectLead = async () => {
    try {
      const userId = getUserIdFromToken();
      const userRes = await getTokenbyData();
      const isAdmin = userRes?.data?.isOverAllAdmin || false;

      // Admins can always edit
      if (isAdmin) {
        setIsProjectLead(true);
        return;
      }

      // Get the project ID from selectedData
      const projectId = typeof selectedData.project === 'object'
        ? selectedData.project._id
        : selectedData.project;

      if (!projectId || !userId) {
        setIsProjectLead(false);
        return;
      }

      // Fetch project details to check project leads
      const res = await getProject({ isEnabled: true });
      const project = res.data.data.find(p => p._id === projectId);

      if (!project) {
        setIsProjectLead(false);
        return;
      }

      // Check if user is in the projectLead array
      const isLead = project.projectLead?.some((lead) => {
        const leadId = typeof lead === 'object' ? lead._id : lead;
        return leadId === userId;
      });

      setIsProjectLead(isLead);
    } catch (error) {
      console.error('Error checking project lead status:', error);
      setIsProjectLead(false);
    }
  };

  // Get user ID from JWT token
  const getUserIdFromToken = () => {
    if (!accessToken) return null;
    try {
      const decoded = jwtDecode(accessToken);
      return decoded.id;
    } catch (error) {
      console.error("Invalid token", error);
      return null;
    }
  };

  const GetProjectData = async () => {
    try {
      const res = await getProject({ isEnabled: true });
      const userId = getUserIdFromToken();

      // Get current user data to check if they are admin
      const userRes = await getTokenbyData();
      const isAdmin = userRes?.data?.isOverAllAdmin || false;

      let filteredProjects = res.data.data;

      // If user is not admin, filter projects where user is a project lead
      if (!isAdmin && userId) {
        filteredProjects = res.data.data.filter((project) => {
          // Check if user is in the projectLead array
          return project.projectLead?.some((lead) => {
            // Handle both object and string ID formats
            const leadId = typeof lead === 'object' ? lead._id : lead;
            return leadId === userId;
          });
        });
      }

      const configData = filteredProjects.map((e) => {
        return {
          ...e,
          label: e.name,
        };
      });
      setProjectData(configData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    GetProjectData();
  }, []);

  const onSubmit = async (values) => {
    try {
      const apiMethod =
        formType === "edit"
          ? updateParentTask(selectedData._id, values)
          : postParentTask(values);
      const res = await apiMethod;
      if (res?.data?.error) {
        ToastError(res?.data?.message);
      }
      setOpenDrawer(false);
      ToastSuccess(res.message);
      GetData();
    } catch (error) {
      ToastError(error?.data?.message);
    }
  };
  return (
    <div className="addRenderingPopup">
      <Box className="addContainerHeader">
        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
          {formType === "edit"
            ? "Edit "
            : formType === "add"
              ? "Add "
              : "View "}
          Part Task
        </Typography>
        <CloseIcon
          className="Close-Icon"
          onClick={() => setOpenDrawer(false)}
        />
      </Box>
      <Divider sx={{ opacity: 1 }} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onSubmit}
        innerRef={formRef}
      >
        {({ touched, errors, setFieldValue, values }) => (
          <Form>
            <Box className="addContainerCenter" mb={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} mt={1}>
                  <div className="wsh-form-control">
                    <Typography className="inputLabel">
                      Project <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Autocomplete
                      options={projectData}
                      value={
                        projectData.find(
                          (option) => option._id === values.project
                        ) || null
                      }
                      disabled={formType === "view" || formType === "edit"}
                      getOptionLabel={(option) => option.label || ""}
                      onChange={(e, v) => {
                        setFieldValue("project", v?._id);
                        setFieldValue("projectType", "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          focused={false}
                          placeholder="Select Project"
                          className="AutoComplete_InputBox"
                        />
                      )}
                    />

                    <div className="form-error">
                      {touched.project && errors.project && errors.project}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={12} mt={1}>
                  <div className="wsh-form-control">
                    <Typography className="inputLabel">
                      Project Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Autocomplete
                      options={
                        values.project
                          ? projectData.find((p) => p._id === values.project)
                            ?.projectType || []
                          : []
                      }
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        (
                          (values.project
                            ? projectData.find((p) => p._id === values.project)
                              ?.projectType
                            : []) || []
                        ).find((type) => type._id === values.projectType) ||
                        null
                      }
                      onChange={(e, newValue) => {
                        setFieldValue("projectType", newValue?._id || "");
                      }}
                      disabled={formType === "view" || formType === "edit"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          focused={false}
                          placeholder="Select Project Type"
                          className="AutoComplete_InputBox"
                        />
                      )}
                    />
                    <div className="form-error">
                      {touched.projectType &&
                        errors.projectType &&
                        errors.projectType}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={12} mt={1}>
                  <Typography className="inputLabel">
                    Title <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="title"
                    className="InputField"
                    placeholder="Enter Title"
                    disabled={formType === "view" || (formType === "edit" && !isProjectLead)}
                  />
                  <div className="form-error">
                    {touched.title && errors.title && errors.title}
                  </div>
                </Grid>

                <Grid item xs={12} sm={12} mt={1}>
                  <Typography className="inputLabel">
                    Description <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="description"
                    className="InputField"
                    placeholder="Enter Description"
                    disabled={formType === "view" || (formType === "edit" && !isProjectLead)}
                  />
                  <div className="form-error">
                    {touched.description &&
                      errors.description &&
                      errors.description}
                  </div>
                </Grid>
              </Grid>
            </Box>
            <Grid className="addboxFooter">
              <Button
                className="cancelButton"
                onClick={() => setOpenDrawer(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="saveButton"
                sx={{ display: formType === "view" || (formType === "edit" && !isProjectLead) ? "none" : "flex" }}
              >
                Save
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddParentTask;
